import React, { useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// OtpInput — Segmented 6-digit code input for one-time passwords
// ---------------------------------------------------------------------------
// Uses the "hidden input" pattern: a single transparent <input> sits on top
// of six visual <div> boxes. The real input captures all keyboard, paste,
// and autofill events natively, while the boxes provide the split-digit
// visual treatment.
//
// Why this approach?
//   Earlier versions used six individual <input maxLength={1}> elements.
//   This caused several issues:
//     • maxLength blocked onChange when a box already had a digit, so
//       auto-advance to the next box didn't fire.
//     • The browser could select the existing digit, causing the next
//       keystroke to overwrite rather than advance.
//     • Complex focus-management code was needed for backspace, arrows,
//       paste, and gap-prevention.
//   A single hidden input avoids all of these because the browser handles
//   cursor movement, selection, paste, and backspace natively — exactly
//   like typing into a regular text field.
//
// This component is shared across all MFA flows:
//   - Provider MFA setup (SetUpMFA.js)
//   - Patient MFA setup (PatientSetUpMFA.js)
//   - Sign-in MFA challenge dialog (MfaDialog.js)
//
// Props:
//   value      – string of 0–6 digit chars (the verification code)
//   onChange   – called with the updated string whenever it changes
//   disabled   – disables the input (e.g. while a request is in flight)
//   autoFocus  – auto-focus the input on mount
//   label      – optional label rendered above the boxes
// ---------------------------------------------------------------------------
export default function OtpInput({ value = '', onChange, disabled = false, autoFocus = false, label }) {
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    // Pad the value into a 6-element array for visual rendering.
    const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

    // The "active" box is the cursor position — the first unfilled slot,
    // clamped to the last box when all 6 digits are entered.
    const activeIndex = Math.min(value.length, 5);

    // Strip non-digits and cap at 6 characters.
    const handleChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 6);
        onChange(raw);
    };

    // Clicking anywhere on the visual boxes focuses the hidden input.
    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    return (
        <div>
            {label && (
                <label className="block text-xs font-bold text-gray-700 mb-2 text-left">
                    {label}
                </label>
            )}
            {/* Container is position:relative so the hidden input can overlay the boxes */}
            <div className="relative cursor-text" onClick={handleContainerClick}>
                {/* Hidden input — captures all keyboard, paste, and autofill events
                    natively. Positioned absolutely over the visual boxes; fully
                    transparent so the styled digit boxes show through. */}
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={value}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete="one-time-code"
                    className="absolute inset-0 w-full h-full opacity-0 z-10"
                    style={{ caretColor: 'transparent' }}
                    aria-label="Verification code"
                />
                {/* Visual digit boxes — purely presentational */}
                <div className="flex justify-center gap-2" aria-hidden="true">
                    {digits.map((digit, i) => {
                        const isActive = isFocused && i === activeIndex;
                        // Determine border + background based on state priority:
                        // disabled > active (focused) > filled > empty
                        const boxStyle = disabled
                            ? 'border-gray-200 bg-gray-50 opacity-50'
                            : isActive
                                ? `border-primary ring-2 ring-primary/20 ${digit ? 'bg-primary/5' : 'bg-white'}`
                                : digit
                                    ? 'border-primary/40 bg-primary/5'
                                    : 'border-gray-200 bg-white';

                        return (
                            <div
                                key={i}
                                className={`
                                    w-12 h-16 flex items-center justify-center
                                    text-lg font-semibold select-none
                                    border-2 rounded-lg
                                    transition-all duration-150
                                    ${boxStyle}
                                `}
                            >
                                {/* Show digit if present, blinking cursor if active + empty */}
                                {digit || (isActive ? (
                                    <span className="w-0.5 h-5 bg-primary animate-pulse rounded-full" />
                                ) : null)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
